import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { radiologyReportService } from '../../lib/services.radiology';
import { CheckCircle, XCircle } from 'lucide-react';

export default function RadiologyPublicVerify() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [report, setReport] = useState<any>(null);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyReport();
  }, [reportId, token]);

  const verifyReport = async () => {
    if (!reportId || !token) {
      setLoading(false);
      return;
    }

    try {
      const reportData = await radiologyReportService.verifyByToken(reportId, token);
      if (reportData) {
        setReport(reportData);
        setVerified(true);
      }
    } catch (error) {
      console.error('Verification failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">Verifying...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            {verified ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Report Verified</h1>
                <p className="text-gray-600">This radiology report is authentic</p>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
                <p className="text-gray-600">Unable to verify this report</p>
              </>
            )}
          </div>

          {verified && report && (
            <div className="border-t border-gray-200 pt-6 space-y-4">
              <div>
                <div className="text-sm text-gray-600">Report Number</div>
                <div className="font-medium">{report.report_no}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Modality</div>
                <div className="font-medium">{report.modality || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Finalized At</div>
                <div className="font-medium">
                  {report.finalized_at ? new Date(report.finalized_at).toLocaleString() : 'N/A'}
                </div>
              </div>
              {report.impression && (
                <div>
                  <div className="text-sm text-gray-600 mb-1">Impression</div>
                  <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded">
                    {report.impression}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
